import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api, EarningsResponse, SettlementStatement, VenueSettlementSummary } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import ConnectPanel, { StripeStatus } from './payments/ConnectPanel';
import EarningsPanel from './payments/EarningsPanel';
import PlatformSettlementPanel from './payments/PlatformSettlementPanel';

const PORTAL_BASE_URL = window.location.origin;

function getPreviousMonths(count: number): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
}

export default function PaymentsScreen() {
  const { business, loading: bizLoading, needsVenueSelection } = useBusiness();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stripeStatus, setStripeStatus] = useState<StripeStatus>(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [settlement, setSettlement] = useState<VenueSettlementSummary | null>(null);
  const [statements, setStatements] = useState<SettlementStatement[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [earnings, setEarnings] = useState<EarningsResponse | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(true);

  const months = getPreviousMonths(6);

  const loadStripeStatus = useCallback(async () => {
    try {
      const status = await api.getStripeConnectStatus();
      setStripeStatus(status);
    } catch {
      setStripeStatus(null);
    } finally {
      setStripeLoading(false);
    }
  }, []);

  const loadSettlement = useCallback(async () => {
    if (!business?.id) {
      setSettlement(null);
      return;
    }
    try {
      setSettlement(await api.getVenueSettlementSummary(business.id));
    } catch {
      setSettlement(null);
    }
    try {
      const { statements: rows } = await api.getVenueSettlementStatements(business.id);
      setStatements(rows);
    } catch {
      setStatements([]);
    }
  }, [business?.id]);

  const loadEarnings = useCallback(async (month: string) => {
    setEarningsLoading(true);
    try {
      setEarnings(await api.getPaymentsEarnings(month));
    } catch {
      setEarnings(null);
    } finally {
      setEarningsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) navigate('/superadmin');
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  useEffect(() => {
    if (searchParams.get('stripe_return') || searchParams.get('stripe_refresh')) {
      loadStripeStatus();
    }
  }, [searchParams, loadStripeStatus]);

  useEffect(() => {
    if (!bizLoading) {
      loadStripeStatus();
      loadSettlement();
      loadEarnings(selectedMonth);
    }
  }, [bizLoading, loadStripeStatus, loadSettlement, loadEarnings, selectedMonth]);

  const handleStartOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      if (!stripeStatus?.connectedAccountId) {
        await api.createStripeConnectAccount();
      }
      const returnUrl = `${PORTAL_BASE_URL}/payments?stripe_return=1`;
      const refreshUrl = `${PORTAL_BASE_URL}/payments?stripe_refresh=1`;
      const { url } = await api.getStripeOnboardingLink(returnUrl, refreshUrl);
      window.location.href = url;
    } catch (err: any) {
      alert(err.message || 'Failed to start Stripe onboarding');
      setOnboardingLoading(false);
    }
  };

  if (bizLoading) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}><div className="spinner" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header">
        <div>
          <h1>Payments</h1>
          <p>Manage your Stripe account and view earnings</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {isSuperAdmin && (
          <RoutingToggle
            businessId={business?.id || null}
            paymentRouting={settlement?.paymentRouting ?? business?.paymentRouting ?? 'connect'}
            settlementCadence={settlement?.settlementCadence ?? 'weekly'}
            onReload={async () => {
              await loadSettlement();
              await loadStripeStatus();
            }}
          />
        )}
        {/* Fall back to the venue's own routing flag so a failed settlement
            fetch can never show a platform-collect venue the Stripe Connect
            card (which would read as false "connect your Stripe" guidance). */}
        {(settlement?.paymentRouting ?? business?.paymentRouting) === 'platform'
          ? <PlatformSettlementPanel settlement={settlement} statements={statements} />
          : (
            <ConnectPanel
              stripeStatus={stripeStatus}
              stripeLoading={stripeLoading}
              onboardingLoading={onboardingLoading}
              onStartOnboarding={handleStartOnboarding}
            />
          )}
        <EarningsPanel
          earnings={earnings}
          earningsLoading={earningsLoading}
          selectedMonth={selectedMonth}
          months={months}
          timezone={business?.timezone || 'UTC'}
          onMonthChange={setSelectedMonth}
        />
      </div>
    </DashboardLayout>
  );
}

function RoutingToggle({
  businessId,
  paymentRouting,
  settlementCadence,
  onReload,
}: {
  businessId: string | null;
  paymentRouting: 'connect' | 'platform';
  settlementCadence: 'weekly' | 'monthly';
  onReload: () => Promise<void>;
}) {
  if (!businessId) return null;

  return (
    <div className="card" style={{ padding: '20px 24px', border: '1px dashed var(--gray-300)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
            Payment Routing <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 500 }}>- Superadmin</span>
          </h3>
          <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: 6, maxWidth: 600, lineHeight: 1.5 }}>
            {paymentRouting === 'platform'
              ? `Customer payments collect into PickleCourts and roll up ${settlementCadence}. Switching back to Connect requires fresh Stripe onboarding.`
              : 'Customer payments go directly to the venue\'s connected Stripe account. Switch to Platform-collect only after you\'ve signed a collection-agent agreement with this venue.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${paymentRouting === 'connect' ? 'btn-primary' : 'btn-secondary'}`}
            disabled={paymentRouting === 'connect'}
            onClick={async () => {
              if (!window.confirm('Flip this venue back to Stripe Connect routing?')) return;
              try {
                await api.setSuperadminVenuePaymentRouting(businessId, 'connect');
                await onReload();
              } catch (err: any) {
                alert(err.message || 'Failed to update payment routing');
              }
            }}
          >
            Stripe Connect
          </button>
          <button
            className={`btn ${paymentRouting === 'platform' ? 'btn-primary' : 'btn-secondary'}`}
            disabled={paymentRouting === 'platform'}
            onClick={async () => {
              if (!window.confirm(`Flip this venue to Platform-collect? Customer payments will land in PickleCourts and roll up ${settlementCadence}.`)) return;
              try {
                await api.setSuperadminVenuePaymentRouting(businessId, 'platform');
                await onReload();
              } catch (err: any) {
                alert(err.message || 'Failed to update payment routing');
              }
            }}
          >
            Platform-collect
          </button>
        </div>
      </div>
    </div>
  );
}
